import { SceneRenderer } from './SceneRenderer';
import { Context } from '../context/Context';
import { Scene } from '../scene/Scene';
import { Camera } from '../camera';
import { RenderMode, RenderSettings } from './index';
import { FullscreenQuad } from './FullscreenQuad';
import { SSAOPass } from './SSAOPass';
import { GodRaysPass } from './GodRaysPass';
import { VolumetricPass } from './VolumetricPass';
import { AerialPerspectivePass } from './AerialPerspectivePass';
import { CloudVolumePass } from './CloudVolumePass';
import { FlamesPass } from './FlamesPass';
import { ProceduralSky } from '../object/primitives/ProceduralSky';
import { DirectionalLight, PointLight, SpotLight, setPointShadowMapSize } from '../light';
import { DrawableObject } from './index';
import { FxaaPass } from './FxaaPass';
import { TaaPass } from './TaaPass';
import { GraphicsSettings, defaultGraphicsSettings } from './GraphicsSettings';
import { BloomPass } from './BloomPass';
import { FinalPresentPass } from './FinalPresentPass';
import { OverlayPass } from '../overlay/OverlayPass';
import { GBufferPass } from './passes/GBufferPass';
import { GBuffer } from './GBuffer';
import { LightingPass, SharedLightData } from './passes/LightingPass';
import { GpuTimer } from '../development/GpuTimer';
import { RenderObject } from '../object/RenderObject';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';

export type RenderStats = {
  totalMs: number;
  gbufferMs: number;
  ssaoMs: number;
  lightingMs: number;
  volumetricMs: number;
  godRaysMs: number;
  aerialPerspectiveMs: number;
  cloudVolumesMs: number;
  bloomMs: number;
  fxaaMs: number;
  taaMs: number;
  transparencyMs: number;
  // In-world overlay quads (health bars, name plates, MSDF text) drawn after
  // FXAA, reading the gbuffer depth for per-fragment occlusion.
  overlayQuadMs: number;
  // Debug visualizations rendered in RenderMode.Overlay (skeleton debug,
  // bone-hit ray markers, etc.). Separate from overlayQuad because they're
  // a different code path (per-object render(), not a fused pass).
  overlayDebugMs: number;
  // CPU work that runs between/around the timed render passes — previously
  // invisible to the perf HUD. Engine populates tickMs (covers the engine and
  // scene tick event managers); the renderer populates the rest.
  tickMs: number;
  // Sub-breakdown of tickMs. engine = engine-level tick handlers (input,
  // FPSCounter); the rest are inside the scene tick. sceneOther catches
  // handlers registered outside Scene.setupTickEvents (PlayerController,
  // NavigationAwareObject, stress test lights, skeleton debug overlay) so
  // engine + lightAnim + objAnim + features + decals + physics +
  // terrainStream + objectStream + sceneOther sums to tickMs.
  tickBreakdown: {
    engine: number;
    lightAnim: number;
    objAnim: number;
    features: number;
    decals: number;
    physics: number;
    terrainStream: number;
    objectStream: number;
    sceneOther: number;
  };
  // Streaming counters captured outside the timed `tickBreakdown` window —
  // `loadTile` continuations resolve asynchronously after the worker returns,
  // so the GltfMesh / SurfaceIndexMap GL creation + rasterizeTile work isn't
  // visible in `terrainStream` ms above. These rows are the per-frame
  // "tiles streamed" signal; pair with `longTaskTotalMs` to confirm whether
  // streaming caused a recent main-thread block.
  streaming: {
    terrainInstalls: number;
    terrainInstallMs: number;
    terrainMaxInstallMs: number;
    terrainEvicts: number;
    terrainEvictMs: number;
    terrainMaxEvictMs: number;
  };
  cullingMs: number;
  // Sub-breakdown of cullingMs (debugging only — not displayed by default).
  cullBreakdown: {
    listWalkMs: number;
    octreeQueryMs: number;
    octreeCacheFilterMs: number;
    listResultCount: number;
    octreeReturnedCount: number;
    totalObjectCount: number;
  };
  instancePrepMs: number;
  // Sub-breakdown of instancePrepMs (mirrors cullBreakdown). Populated from
  // InstanceManager.prepareStats after each prepareFrame call. Not displayed
  // in the perf overlay; read directly off renderStats.prepareBreakdown when
  // profiling. Sub-timers sum to ~instancePrepMs (lodPartition + matrixCopy +
  // upload + bufferGrow + partition).
  prepareBreakdown: {
    partitionMs: number;
    lodPartitionMs: number;
    matrixCopyMs: number;
    bufferGrowMs: number;
    uploadMs: number;
    groupsTouched: number;
    instancesUploaded: number;
  };
  // Strict subset of lightingMs (CPU side of TileLightAssigner.assign).
  // Excluded from cpuTotal in the HUD to avoid double-counting.
  tileAssignMs: number;
  // Shadow pass is invoked from Engine.renderFrame before this renderer runs;
  // the engine populates shadowCpuMs / shadowGpuMs into our stats so the
  // FPS overlay can read everything from one place.
  shadowCpuMs: number;
  shadowGpuMs: number;
  // Sub-breakdown of shadowCpuMs (mirrors cullBreakdown). Populated from
  // LightManager.shadowStats after each renderShadowMaps call.
  shadowBreakdown: {
    lightLoopMs: number;
    getNonStaticMs: number;
    renderStaticMs: number;
    renderDynamicMs: number;
    dirtyCasters: number;
    shadowMapsRendered: number;
  };
  // Bitmask of CSM cascades that rebuilt their static-caster cache this
  // frame (bit i ↔ cascade i). Sourced from
  // `DirectionalLight.cascadedShadowMap.lastStaticCacheRebuildMask` after
  // the shadow pass runs. 0 = pure cache hit; nonzero on every frame while
  // moving = the dominant shadow cost.
  csmStaticRebuildMask: number;
  // GPU-side equivalents of the *Ms fields above. Populated from the GPU
  // timer ring buffer; lag the CPU figures by 1-3 frames and read 0 when
  // EXT_disjoint_timer_query_webgl2 is unavailable.
  gbufferGpuMs: number;
  ssaoGpuMs: number;
  lightingGpuMs: number;
  volumetricGpuMs: number;
  godRaysGpuMs: number;
  aerialPerspectiveGpuMs: number;
  cloudVolumesGpuMs: number;
  bloomGpuMs: number;
  fxaaGpuMs: number;
  taaGpuMs: number;
  transparencyGpuMs: number;
  overlayQuadGpuMs: number;
  overlayDebugGpuMs: number;
  // Whole-frame GPU time (shadow + all deferred passes + overlays). Sourced
  // from a single TIME_ELAPSED query that runs on alternating frames in
  // place of per-pass queries (the two cannot nest). On macOS ANGLE/Metal
  // this is the trustworthy GPU number — per-pass values there are often
  // inflated/aggregated by the driver. Populated by Engine.renderFrame.
  frameGpuMs: number;
  shaderSwitches: number;
  instancedDrawCalls: number;
  instancedObjects: number;
  renderedFaces: number;
  // Per-pass run flags. Set every frame based on the renderer's actual
  // decisions (settings + sun-gating + TAA/FXAA exclusion). Stable signal
  // for UI to mark passes as active without watching for >0 timings, which
  // can flicker when CPU times round to 0 or GPU timer results lag.
  shadowRan: boolean;
  gbufferRan: boolean;
  ssaoRan: boolean;
  lightingRan: boolean;
  volumetricRan: boolean;
  godRaysRan: boolean;
  aerialPerspectiveRan: boolean;
  cloudVolumesRan: boolean;
  transparencyRan: boolean;
  taaRan: boolean;
  bloomRan: boolean;
  fxaaRan: boolean;
};

export class DeferredSceneRenderer extends SceneRenderer {
  private ctx!: Context;
  private gBufferPass!: GBufferPass;
  private lightingPass!: LightingPass;
  private quad!: FullscreenQuad;
  private ssaoPass!: SSAOPass;
  private godRaysPass!: GodRaysPass;
  private volumetricPass!: VolumetricPass;
  private aerialPerspectivePass!: AerialPerspectivePass;
  private cloudVolumePass!: CloudVolumePass;
  private flamesPass!: FlamesPass;
  private fxaaPass!: FxaaPass;
  private taaPass!: TaaPass;
  private bloomPass: BloomPass | null = null;
  private finalPresentPass!: FinalPresentPass;
  private overlayPass!: OverlayPass;
  private gpuTimer!: GpuTimer;
  private _settings: GraphicsSettings = defaultGraphicsSettings();

  // Always-allocated HDR scene FBO. Every opaque/transparent pass renders into
  // this; bloom additively blends glow back into it; FinalPresentPass tonemaps
  // it to the LDR canvas (or the FXAA input FBO). Decouples tonemapping from
  // bloom so every graphics preset combination produces a properly tonemapped
  // image.
  private hdrSceneFbo!: WebGLFramebuffer;
  private hdrSceneTexture!: RenderTexture;
  private hdrSceneDepth!: WebGLRenderbuffer;

  // Toggle periodic console.log of cull/shadow/prepare breakdown stats.
  // When true, dumps a 3-line block every `perfLogIntervalFrames` frames.
  // Off by default; flip from devtools when investigating per-frame CPU:
  //   engine.getDeferredRenderer().perfLogEnabled = true
  public perfLogEnabled = false;
  public perfLogIntervalFrames = 120;
  private perfLogFrameCounter = 0;

  public get graphicsSettings(): Readonly<GraphicsSettings> {
    return this._settings;
  }

  public get timer(): GpuTimer {
    return this.gpuTimer;
  }

  /**
   * Exposes the geometry-pass G-buffer so other renderers (notably picking)
   * can sample or blit from its depth attachment. `undefined` before init().
   */
  public get gBuffer(): GBuffer | undefined {
    return this.gBufferPass?.gBuffer;
  }

  public get ssaoEnabled(): boolean {
    return this._settings.ssaoEnabled;
  }
  public set ssaoEnabled(v: boolean) {
    this._settings.ssaoEnabled = v;
  }

  public get godRaysEnabled(): boolean {
    return this._settings.godRaysEnabled;
  }
  public set godRaysEnabled(v: boolean) {
    this._settings.godRaysEnabled = v;
  }

  public get volumetricEnabled(): boolean {
    return this._settings.volumetricEnabled;
  }
  public set volumetricEnabled(v: boolean) {
    this._settings.volumetricEnabled = v;
  }

  public get aerialPerspectiveEnabled(): boolean {
    return this._settings.aerialPerspectiveEnabled;
  }
  public set aerialPerspectiveEnabled(v: boolean) {
    this._settings.aerialPerspectiveEnabled = v;
  }

  public get cloudVolumesEnabled(): boolean {
    return this._settings.cloudVolumesEnabled;
  }
  public set cloudVolumesEnabled(v: boolean) {
    this._settings.cloudVolumesEnabled = v;
  }

  public get fxaaEnabled(): boolean {
    return this._settings.fxaaEnabled;
  }
  public set fxaaEnabled(v: boolean) {
    this._settings.fxaaEnabled = v;
  }

  public get taaEnabled(): boolean {
    return this._settings.taaEnabled;
  }
  public set taaEnabled(v: boolean) {
    this._settings.taaEnabled = v;
  }

  public get bloomEnabled(): boolean {
    return this._settings.bloomEnabled;
  }
  public set bloomEnabled(v: boolean) {
    this._settings.bloomEnabled = v;
  }

  private getBloomPass(): BloomPass {
    if (!this.bloomPass) {
      this.bloomPass = new BloomPass();
      this.bloomPass.init(this.ctx);
      this.bloomPass.threshold = this._settings.bloomThreshold;
      this.bloomPass.strength = this._settings.bloomStrength;
    }
    return this.bloomPass;
  }

  private allocHdrSceneFbo(ctx: Context): void {
    const gl = ctx.gl;
    const w = ctx.viewPort.width;
    const h = ctx.viewPort.height;

    this.hdrSceneTexture = new RenderTexture();
    this.hdrSceneTexture.glTexture = createColorTexture(
      gl,
      w,
      h,
      gl.RGBA16F,
      gl.RGBA,
      gl.HALF_FLOAT,
    );
    this.hdrSceneFbo = createFramebuffer(gl, this.hdrSceneTexture.glTexture!);

    this.hdrSceneDepth = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.hdrSceneDepth);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.hdrSceneFbo);
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.RENDERBUFFER,
      this.hdrSceneDepth,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  public renderStats: RenderStats = {
    totalMs: 0,
    gbufferMs: 0,
    ssaoMs: 0,
    lightingMs: 0,
    volumetricMs: 0,
    godRaysMs: 0,
    aerialPerspectiveMs: 0,
    cloudVolumesMs: 0,
    bloomMs: 0,
    fxaaMs: 0,
    taaMs: 0,
    transparencyMs: 0,
    overlayQuadMs: 0,
    overlayDebugMs: 0,
    tickMs: 0,
    tickBreakdown: {
      engine: 0,
      lightAnim: 0,
      objAnim: 0,
      features: 0,
      decals: 0,
      physics: 0,
      terrainStream: 0,
      objectStream: 0,
      sceneOther: 0,
    },
    streaming: {
      terrainInstalls: 0,
      terrainInstallMs: 0,
      terrainMaxInstallMs: 0,
      terrainEvicts: 0,
      terrainEvictMs: 0,
      terrainMaxEvictMs: 0,
    },
    cullingMs: 0,
    cullBreakdown: {
      listWalkMs: 0,
      octreeQueryMs: 0,
      octreeCacheFilterMs: 0,
      listResultCount: 0,
      octreeReturnedCount: 0,
      totalObjectCount: 0,
    },
    instancePrepMs: 0,
    prepareBreakdown: {
      partitionMs: 0,
      lodPartitionMs: 0,
      matrixCopyMs: 0,
      bufferGrowMs: 0,
      uploadMs: 0,
      groupsTouched: 0,
      instancesUploaded: 0,
    },
    tileAssignMs: 0,
    shadowCpuMs: 0,
    shadowGpuMs: 0,
    shadowBreakdown: {
      lightLoopMs: 0,
      getNonStaticMs: 0,
      renderStaticMs: 0,
      renderDynamicMs: 0,
      dirtyCasters: 0,
      shadowMapsRendered: 0,
    },
    csmStaticRebuildMask: 0,
    gbufferGpuMs: 0,
    ssaoGpuMs: 0,
    lightingGpuMs: 0,
    volumetricGpuMs: 0,
    godRaysGpuMs: 0,
    aerialPerspectiveGpuMs: 0,
    cloudVolumesGpuMs: 0,
    bloomGpuMs: 0,
    fxaaGpuMs: 0,
    taaGpuMs: 0,
    transparencyGpuMs: 0,
    overlayQuadGpuMs: 0,
    overlayDebugGpuMs: 0,
    frameGpuMs: 0,
    shaderSwitches: 0,
    instancedDrawCalls: 0,
    instancedObjects: 0,
    renderedFaces: 0,
    shadowRan: false,
    gbufferRan: false,
    ssaoRan: false,
    lightingRan: false,
    volumetricRan: false,
    godRaysRan: false,
    aerialPerspectiveRan: false,
    cloudVolumesRan: false,
    transparencyRan: false,
    taaRan: false,
    bloomRan: false,
    fxaaRan: false,
  };

  public init(ctx: Context): void {
    this.ctx = ctx;
    this.gpuTimer = new GpuTimer(ctx.gl);
    this.gBufferPass = new GBufferPass();
    this.gBufferPass.init(ctx);
    this.lightingPass = new LightingPass(ctx);
    this.quad = new FullscreenQuad(ctx.gl);
    this.ssaoPass = new SSAOPass();
    this.ssaoPass.init(ctx, this._settings.ssaoResolutionDivisor);
    this.godRaysPass = new GodRaysPass();
    this.godRaysPass.init(ctx, this._settings.godRaysResolutionDivisor);
    this.volumetricPass = new VolumetricPass();
    this.volumetricPass.init(ctx, this._settings.volumetricQuality);
    this.aerialPerspectivePass = new AerialPerspectivePass();
    this.aerialPerspectivePass.init(ctx);
    this.cloudVolumePass = new CloudVolumePass();
    this.cloudVolumePass.init(ctx);
    this.flamesPass = new FlamesPass();
    this.flamesPass.init(ctx);
    this.fxaaPass = new FxaaPass();
    this.fxaaPass.init(ctx);
    this.taaPass = new TaaPass();
    this.taaPass.init(ctx);
    this.finalPresentPass = new FinalPresentPass();
    this.finalPresentPass.init(ctx);
    this.finalPresentPass.exposure = this._settings.exposure;
    this.overlayPass = new OverlayPass();
    this.overlayPass.init(ctx);
    // Always-on HDR scene target. Every opaque/transparent contributor binds
    // this FBO; FinalPresentPass tonemaps the result before FXAA / canvas.
    this.allocHdrSceneFbo(ctx);
    // BloomPass is lazily initialized on first use to avoid allocating its
    // RGBA16F mip chain when bloom is disabled.
    this.bloomPass = null;

    // Apply shadow settings
    setPointShadowMapSize(this._settings.pointShadowMapResolution);
  }

  public applySettings(settings: Partial<GraphicsSettings>): void {
    const prev = { ...this._settings };
    Object.assign(this._settings, settings);
    const s = this._settings;
    const ctx = this.ctx;

    // Render scale — resize canvas and recreate all FBOs
    if (s.renderScale !== prev.renderScale) {
      const cb = ctx.engine.contextBuilder;
      cb.resizeCanvas(cb.displayWidth, cb.displayHeight, s.renderScale);
      this.resize();
      // Don't return — scene-level settings (shadow maps, shadow budget, etc.)
      // still need to be applied below since init() only sets renderer FBOs.
    }

    // SSAO resolution
    if (s.ssaoResolutionDivisor !== prev.ssaoResolutionDivisor) {
      this.ssaoPass.setResolutionDivisor(ctx, s.ssaoResolutionDivisor);
    }

    // God rays resolution
    if (s.godRaysResolutionDivisor !== prev.godRaysResolutionDivisor) {
      this.godRaysPass.setResolutionDivisor(ctx, s.godRaysResolutionDivisor);
    }

    // Volumetric quality
    if (s.volumetricQuality !== prev.volumetricQuality) {
      this.volumetricPass.setQuality(ctx, s.volumetricQuality);
    }

    // Shadow budget
    const scene = ctx.engine.activeScene;
    if (scene && s.shadowBudget !== prev.shadowBudget) {
      scene.lightManager.shadowBudget = s.shadowBudget;
    }

    // Point/spot shadow map size — destroy existing shadow maps so they get
    // recreated at the new resolution. The camera dimensions also need updating
    // since they were captured from SHADOW_MAP_DIMENSIONS at construction time.
    if (s.pointShadowMapResolution !== prev.pointShadowMapResolution) {
      setPointShadowMapSize(s.pointShadowMapResolution);
      if (scene) {
        const newDims = { width: s.pointShadowMapResolution, height: s.pointShadowMapResolution };
        for (const light of scene.lightManager.lights) {
          if (light instanceof PointLight || light instanceof SpotLight) {
            if (light.shadowMap) {
              light.shadowMap.destroy();
              light.shadowMap = undefined;
            }
            light.camera.dimensions = newDims;
            light.shadowDirty = true;
          }
        }
      }
    }

    // Only update bloom params if the pass has been lazily created
    if (this.bloomPass) {
      this.bloomPass.threshold = s.bloomThreshold;
      this.bloomPass.strength = s.bloomStrength;
    }

    // Exposure feeds the always-on FinalPresentPass — drives the tonemap
    // input scale regardless of whether bloom is enabled.
    if (this.finalPresentPass) {
      this.finalPresentPass.exposure = s.exposure;
    }

    // CSM shadow map resolution
    if (s.shadowMapResolution !== prev.shadowMapResolution && scene) {
      for (const light of scene.lightManager.lights) {
        if (light instanceof DirectionalLight) {
          light.setShadowMapResolution(ctx, s.shadowMapResolution);
        }
      }
    }

    // TAA — push jitter setting onto the camera and clear history when toggling.
    if (s.taaEnabled !== prev.taaEnabled) {
      const cam = ctx.engine.activeCamera;
      cam.taaJitterEnabled = s.taaEnabled;
      cam.invalidateHistory();
    }
  }

  public override resize(): void {
    if (!this.ctx) return;
    this.destroy();
    this.init(this.ctx);
    // Recreated history buffers are empty; the next frame must skip blending.
    this.ctx.engine.activeCamera?.invalidateHistory();
  }

  public destroy(): void {
    this.gBufferPass?.destroy(this.ctx);
    this.lightingPass?.destroy();
    this.ssaoPass?.destroy(this.ctx.gl);
    this.godRaysPass?.destroy(this.ctx.gl);
    this.volumetricPass?.destroy(this.ctx.gl);
    this.aerialPerspectivePass?.destroy(this.ctx.gl);
    this.cloudVolumePass?.destroy(this.ctx.gl);
    this.flamesPass?.destroy(this.ctx.gl);
    this.fxaaPass?.destroy(this.ctx.gl);
    this.taaPass?.destroy(this.ctx.gl);
    this.finalPresentPass?.destroy(this.ctx.gl);
    this.overlayPass?.destroy(this.ctx.gl);
    this.bloomPass?.destroy(this.ctx.gl);
    if (this.hdrSceneFbo) {
      this.ctx.gl.deleteFramebuffer(this.hdrSceneFbo);
    }
    if (this.hdrSceneDepth) {
      this.ctx.gl.deleteRenderbuffer(this.hdrSceneDepth);
    }
    if (this.hdrSceneTexture?.glTexture) {
      this.ctx.gl.deleteTexture(this.hdrSceneTexture.glTexture);
    }
    this.gpuTimer?.destroy();
  }

  public invalidateTemporalHistory(): void {
    if (this.ctx?.engine.activeCamera) {
      this.ctx.engine.activeCamera.invalidateHistory();
    }
  }

  public render(scene: Scene, camera: Camera, buffer: WebGLFramebuffer | null): void {
    const ctx = this.ctx;
    const gl = ctx.gl;
    const stats = this.renderStats;
    const frameStart = performance.now();
    ctx.shaderSwitchCount = 0;
    scene.objectManager.invalidateVisibilityCache();
    const timer = this.gpuTimer;
    // Note: timer.beginFrame() is called by Engine.renderFrame before the
    // shadow pass. We don't call it again here — disjoint detection should
    // happen once per frame, not once per render call.

    // Get objects: GBuffer mode excludes skybox (which has modes=[Primary])
    scene.objectManager.resetCullStats();
    const cullStart = performance.now();
    const gbufferObjects = scene.objectManager.getAllVisibleObjects(
      RenderMode.GBuffer,
      camera.frustum,
    );
    const primaryObjects = scene.objectManager.getAllVisibleObjects(
      RenderMode.Primary,
      camera.frustum,
    );
    const lights = scene.lightManager.getVisibleLights(camera.frustum);
    stats.cullingMs = performance.now() - cullStart;
    {
      const c = scene.objectManager.cullStats;
      const b = stats.cullBreakdown;
      b.listWalkMs = c.listWalkMs;
      b.octreeQueryMs = c.octreeQueryMs;
      b.octreeCacheFilterMs = c.octreeCacheFilterMs;
      b.listResultCount = c.listResultCount;
      b.octreeReturnedCount = c.octreeReturnedCount;
      b.totalObjectCount = c.totalObjectCount;
    }

    // === 1. G-buffer geometry pass ===
    // Partition into instanced groups and non-instanced objects
    const instancePrepStart = performance.now();
    const { instanceGroups, nonInstancedObjects } = scene.instanceManager.prepareFrame(
      gl,
      gbufferObjects,
      RenderMode.GBuffer,
      camera.cameraPosition,
      this._settings.lodDistanceScale,
      this._settings.lodCrossfadeEnabled,
    );

    stats.instancedDrawCalls = instanceGroups.length;
    stats.instancedObjects = instanceGroups.reduce((sum, g) => sum + g.visibleCount, 0);

    let renderedFaces = instanceGroups.reduce((sum, g) => sum + g.renderedFaceCount, 0);
    for (const obj of nonInstancedObjects) {
      if (obj instanceof RenderObject) {
        renderedFaces += Math.floor((obj.mesh.indexBuffer?.itemCount ?? 0) / 3);
      }
    }
    stats.renderedFaces = renderedFaces;

    // Sort non-instanced by shader variant to minimize gl.useProgram() switches
    nonInstancedObjects.sort(
      (a, b) =>
        (a.getShaderSortKey?.(RenderMode.GBuffer) ?? 0) -
        (b.getShaderSortKey?.(RenderMode.GBuffer) ?? 0),
    );
    stats.instancePrepMs = performance.now() - instancePrepStart;
    {
      const p = scene.instanceManager.prepareStats;
      const b = stats.prepareBreakdown;
      b.partitionMs = p.partitionMs;
      b.lodPartitionMs = p.lodPartitionMs;
      b.matrixCopyMs = p.matrixCopyMs;
      b.bufferGrowMs = p.bufferGrowMs;
      b.uploadMs = p.uploadMs;
      b.groupsTouched = p.groupsTouched;
      b.instancesUploaded = p.instancesUploaded;
    }

    let t0 = performance.now();
    timer.begin('gbuffer');
    this.gBufferPass.render(
      ctx,
      scene,
      camera,
      instanceGroups,
      nonInstancedObjects,
      this._settings.lodCrossfadeEnabled,
    );
    timer.end();
    stats.gbufferMs = performance.now() - t0;
    stats.gbufferRan = true;

    // === 1b. SSAO pass (half-resolution, reads G-buffer) ===
    t0 = performance.now();
    stats.ssaoRan = this.ssaoEnabled;
    if (this.ssaoEnabled) {
      timer.begin('ssao');
      this.ssaoPass.renderSSAO(ctx, this.gBufferPass.gBuffer, this.quad, camera);
      timer.end();
    }
    stats.ssaoMs = performance.now() - t0;

    // === 2. Bind output framebuffer and clear ===
    // Render chain (every preset combination):
    //   opaque → hdrSceneFbo (RGBA16F, always allocated)
    //   [TAA]  → reads hdrSceneTexture, writes back into hdrSceneFbo
    //   [bloom] → reads hdrSceneTexture, additively blends glow into hdrSceneFbo
    //   FinalPresent (always) → ACES tonemap hdrSceneTexture → presentTarget
    //   [FXAA] → reads fxaaPass FBO, writes canvas
    //   Overlay (always) → canvas
    //
    // TAA already produces soft AA via accumulation; running FXAA after it
    // hunts for edges TAA already smoothed and over-sharpens them, so treat
    // FXAA as disabled whenever TAA is on. Engine flags stay independent so
    // debug paths can still force-enable both — only the render chain
    // enforces exclusion.
    const useFxaa = this.fxaaEnabled && !this.taaEnabled;
    const opaqueTarget = this.hdrSceneFbo;
    const presentTarget = useFxaa ? this.fxaaPass.getFramebuffer() : buffer;
    gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    // === 3. Render skybox as background (Primary-only objects) ===
    // Skybox renders with depthMask(false), providing background color
    const skyboxSettings: RenderSettings = {
      mode: RenderMode.Primary,
      lights: [],
      objects: primaryObjects,
    };
    for (const obj of primaryObjects) {
      // Only render objects that are NOT in the GBuffer set (i.e., skybox).
      // Objects with GBuffer mode are already rendered in the G-buffer pass.
      if (!obj.renderConfiguration.modes?.includes(RenderMode.GBuffer)) {
        obj.render(ctx, camera, skyboxSettings);
      }
    }

    // === 4. Light passes ===
    t0 = performance.now();
    timer.begin('lighting');
    const sharedLightData = this.lightingPass.render(
      ctx,
      scene,
      camera,
      lights,
      this.gBufferPass.gBuffer,
      this.quad,
      this.ssaoPass,
      this.ssaoEnabled,
    );
    timer.end();
    stats.lightingMs = performance.now() - t0;
    stats.tileAssignMs = this.lightingPass.tileAssignMs;
    stats.lightingRan = true;

    // === 5. Volumetric light shafts ===
    t0 = performance.now();
    let volumetricRan = false;
    const sunLight = lights.find((l): l is DirectionalLight => l instanceof DirectionalLight);
    // Capture how many CSM cascades rebuilt their static cache this frame.
    // A persistently-nonzero mask while the camera is moving means the
    // cascade-center texel-drift threshold is being crossed every frame,
    // which forces a full re-rasterisation of every static shadow caster
    // — the dominant shadowGpu cost over scatter.
    stats.csmStaticRebuildMask = sunLight?.cascadedShadowMap?.lastStaticCacheRebuildMask ?? 0;
    // Sun-driven passes are wasted GPU when the sun isn't actually lighting
    // the scene. Light direction is the way light travels, so direction.y < 0
    // means the sun is above the horizon (light pointing down). DayNightCycle
    // also drives color * intensity to ~0 outside dawn/dusk, so a tiny color
    // floor catches paths that don't update direction.y (manual config, etc.).
    const sunIsLighting =
      !!sunLight &&
      sunLight.direction[1] < 0 &&
      Math.max(sunLight.color[0], sunLight.color[1], sunLight.color[2]) > 0.001;
    if (this.volumetricEnabled && sunLight && sunLight.cascadedShadowMap && sunIsLighting) {
      volumetricRan = true;
      timer.begin('volumetric');
      this.volumetricPass.render(
        ctx,
        this.gBufferPass.gBuffer,
        this.quad,
        camera,
        sunLight,
        opaqueTarget,
      );
      gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
      gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
      timer.end();
    }
    stats.volumetricMs = performance.now() - t0;
    stats.volumetricRan = volumetricRan;

    // === 6. God rays (radial blur) ===
    t0 = performance.now();
    const godRaysRan = !!(this.godRaysEnabled && sunLight && sunIsLighting);
    stats.godRaysRan = godRaysRan;
    if (godRaysRan) {
      ctx.textureState.clear();
      timer.begin('godRays');
      this.godRaysPass.render(
        ctx,
        this.gBufferPass.gBuffer,
        this.quad,
        camera,
        sunLight,
        opaqueTarget,
      );
      // Restore state after god rays
      gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
      gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
      timer.end();
    }
    stats.godRaysMs = performance.now() - t0;

    // === 6b. Aerial perspective ===
    // Blends opaque scene pixels toward the same atmospheric scattering colour
    // the procedural sky shows in their direction, smoothing far-LOD silhouette
    // drift and the hard horizon clip. Only runs when ProceduralSky is active.
    t0 = performance.now();
    const proceduralSky = primaryObjects.find(
      (o): o is ProceduralSky => o instanceof ProceduralSky,
    );
    const aerialRan = !!(this.aerialPerspectiveEnabled && proceduralSky);
    stats.aerialPerspectiveRan = aerialRan;
    if (aerialRan) {
      ctx.textureState.clear();
      timer.begin('aerialPerspective');
      this.aerialPerspectivePass.render(
        ctx,
        this.gBufferPass.gBuffer,
        this.quad,
        camera,
        proceduralSky,
        sunLight,
        opaqueTarget,
      );
      gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
      gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
      timer.end();
    }
    stats.aerialPerspectiveMs = performance.now() - t0;

    // === 6c. Volumetric cloud volumes ===
    // Authored polygon-prism volumes raymarched from the camera and composited
    // over the lit opaque scene. Skipped entirely when the scene has no
    // cloud volumes (cost goes to zero on Sponza / demo without authored
    // clouds). Sits after aerial perspective so the haze is on the geometry
    // *behind* the clouds, then clouds blend over the haze-tinted scene.
    t0 = performance.now();
    const cloudVolumes = scene.cloudVolumes;
    const cloudsRan = !!(this.cloudVolumesEnabled && cloudVolumes.length > 0);
    stats.cloudVolumesRan = cloudsRan;
    if (cloudsRan) {
      ctx.textureState.clear();
      timer.begin('cloudVolumes');
      this.cloudVolumePass.render(
        ctx,
        this.gBufferPass.gBuffer,
        this.quad,
        camera,
        cloudVolumes,
        proceduralSky,
        sunLight,
        performance.now(),
        opaqueTarget,
      );
      gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
      gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
      timer.end();
    }
    stats.cloudVolumesMs = performance.now() - t0;

    // === 7. Forward transparency pass ===
    // Must run BEFORE FXAA so transparent objects share opaqueTarget's depth buffer
    // (the ambient pass wrote gl_FragDepth there). FXAA then anti-aliases everything.
    t0 = performance.now();
    timer.begin('transparency');
    this.renderTransparency(ctx, scene, camera, lights, opaqueTarget, sharedLightData, volumetricRan);
    timer.end();
    stats.transparencyMs = performance.now() - t0;
    stats.transparencyRan = true;

    // === 7a-pre. Procedural flames ===
    // Per-flame additive billboards; sit after transparency so flames
    // composite over both opaque and transparent geometry, and before TAA so
    // the flame body gets temporally smoothed with the rest of the frame.
    // Pass early-outs when scene.flames is empty.
    if (scene.flames.length > 0) {
      ctx.textureState.clear();
      timer.begin('flames');
      this.flamesPass.render(
        ctx,
        this.gBufferPass.gBuffer,
        camera,
        scene.flames,
        performance.now(),
        opaqueTarget,
      );
      gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
      gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
      timer.end();
    }

    // === 7a. Temporal anti-aliasing ===
    // Blends current frame with reprojected history via motion vectors, then
    // composites the result back into hdrSceneFbo so downstream passes (bloom,
    // FinalPresent, FXAA) all see the temporally smoothed image.
    t0 = performance.now();
    stats.taaRan = this.taaEnabled;
    if (this.taaEnabled) {
      timer.begin('taa');
      this.taaPass.render(
        ctx,
        this.quad,
        this.gBufferPass.gBuffer,
        camera,
        this.hdrSceneTexture,
        this.hdrSceneFbo,
      );
      timer.end();
    }
    stats.taaMs = performance.now() - t0;

    // === 7b. Bloom (additive glow into hdrSceneFbo) ===
    t0 = performance.now();
    stats.bloomRan = this.bloomEnabled;
    if (this.bloomEnabled) {
      timer.begin('bloom');
      this.getBloomPass().render(ctx, this.quad, this.hdrSceneTexture, this.hdrSceneFbo);
      timer.end();
    }
    stats.bloomMs = performance.now() - t0;

    // === 7c. FinalPresentPass — ACES tonemap to LDR (always runs) ===
    // Decouples tonemapping from bloom so every preset combination produces a
    // correctly-mapped image. Writes to the FXAA input FBO when FXAA is on,
    // otherwise straight to the canvas.
    this.finalPresentPass.render(ctx, this.quad, this.hdrSceneTexture, presentTarget);

    // === 7d. FXAA pass (LDR edge AA) ===
    t0 = performance.now();
    stats.fxaaRan = useFxaa;
    if (useFxaa) {
      timer.begin('fxaa');
      this.fxaaPass.render(ctx, this.quad, buffer);
      gl.bindFramebuffer(gl.FRAMEBUFFER, buffer);
      gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
      timer.end();
    }
    stats.fxaaMs = performance.now() - t0;

    // === 7e. In-world overlay quads (health bars, name plates, damage numbers) ===
    // Runs after FXAA so text/UI edges aren't softened, and reads the gbuffer
    // depth texture for per-fragment occlusion against world geometry.
    t0 = performance.now();
    timer.begin('overlayQuad');
    this.overlayPass.render(
      ctx,
      camera,
      scene.overlayManager,
      this.gBufferPass.gBuffer.depthTexture,
      buffer,
    );
    timer.end();
    stats.overlayQuadMs = performance.now() - t0;

    // === 8. Overlay pass (debug visualizations rendered on top) ===
    // Culling is accounted to cullingMs (matches every other pass); only the
    // actual render loop counts toward overlayDebugMs. Without this split the
    // culling slice would be double-counted in the HUD's cpuTotal.
    const overlayCullStart = performance.now();
    const overlayObjects = scene.objectManager.getAllVisibleObjects(
      RenderMode.Overlay,
      camera.frustum,
    );
    stats.cullingMs += performance.now() - overlayCullStart;
    const overlayDebugRenderStart = performance.now();
    if (overlayObjects.length > 0) {
      timer.begin('overlayDebug');
      const overlaySettings: RenderSettings = {
        mode: RenderMode.Overlay,
        lights: [],
        objects: overlayObjects,
      };
      for (const obj of overlayObjects) {
        obj.render(ctx, camera, overlaySettings);
      }
      timer.end();
    }
    stats.overlayDebugMs = performance.now() - overlayDebugRenderStart;

    // Restore state
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);

    stats.shaderSwitches = ctx.shaderSwitchCount;
    // Read settled GPU times. These lag CPU times by 1-3 frames; for steady-
    // state frame inspection that's fine. Disabled passes keep their last
    // measurement, which is misleading when toggled mid-session — zero them
    // out explicitly so the overlay shows 0 ms for inactive passes.
    stats.gbufferGpuMs = timer.getLastResultMs('gbuffer');
    stats.ssaoGpuMs = this.ssaoEnabled ? timer.getLastResultMs('ssao') : 0;
    stats.lightingGpuMs = timer.getLastResultMs('lighting');
    stats.volumetricGpuMs = volumetricRan ? timer.getLastResultMs('volumetric') : 0;
    stats.godRaysGpuMs =
      this.godRaysEnabled && sunLight && sunIsLighting ? timer.getLastResultMs('godRays') : 0;
    stats.aerialPerspectiveGpuMs = aerialRan ? timer.getLastResultMs('aerialPerspective') : 0;
    stats.cloudVolumesGpuMs = cloudsRan ? timer.getLastResultMs('cloudVolumes') : 0;
    stats.transparencyGpuMs = timer.getLastResultMs('transparency');
    stats.taaGpuMs = this.taaEnabled ? timer.getLastResultMs('taa') : 0;
    stats.bloomGpuMs = this.bloomEnabled ? timer.getLastResultMs('bloom') : 0;
    stats.fxaaGpuMs = useFxaa ? timer.getLastResultMs('fxaa') : 0;
    stats.overlayQuadGpuMs = timer.getLastResultMs('overlayQuad');
    stats.overlayDebugGpuMs = timer.getLastResultMs('overlayDebug');
    stats.totalMs = performance.now() - frameStart;

    if (this.perfLogEnabled) {
      this.perfLogFrameCounter++;
      if (this.perfLogFrameCounter >= this.perfLogIntervalFrames) {
        this.perfLogFrameCounter = 0;
        const cb = stats.cullBreakdown;
        const sb = stats.shadowBreakdown;
        const pb = stats.prepareBreakdown;
        const f = (n: number) => n.toFixed(2);
        console.log(
          `[cull] total=${f(stats.cullingMs)}ms list=${f(cb.listWalkMs)} octreeQuery=${f(cb.octreeQueryMs)} octreeFilter=${f(cb.octreeCacheFilterMs)} | objs=${cb.totalObjectCount} listOut=${cb.listResultCount} octOut=${cb.octreeReturnedCount}`,
        );
        console.log(
          `[shadow] total=${f(stats.shadowCpuMs)}ms loop=${f(sb.lightLoopMs)} getNonStatic=${f(sb.getNonStaticMs)} static=${f(sb.renderStaticMs)} dynamic=${f(sb.renderDynamicMs)} | dirtyCasters=${sb.dirtyCasters} mapsRendered=${sb.shadowMapsRendered}`,
        );
        console.log(
          `[prepare] total=${f(stats.instancePrepMs)}ms partition=${f(pb.partitionMs)} lodPart=${f(pb.lodPartitionMs)} copy=${f(pb.matrixCopyMs)} upload=${f(pb.uploadMs)} grow=${f(pb.bufferGrowMs)} | groups=${pb.groupsTouched} inst=${pb.instancesUploaded}`,
        );
      }
    }
  }

  private renderTransparency(
    ctx: Context,
    scene: Scene,
    camera: Camera,
    lights: import('../light').Light[],
    opaqueTarget: WebGLFramebuffer | null,
    shared: SharedLightData,
    volumetricRan: boolean,
  ): void {
    const gl = ctx.gl;
    const transparentObjects = scene.objectManager.getAllVisibleObjects(
      RenderMode.Transparent,
      camera.frustum,
    );
    if (transparentObjects.length === 0) return;

    this.sortBackToFront(transparentObjects, camera);

    gl.bindFramebuffer(gl.FRAMEBUFFER, opaqueTarget);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    // Reuse light data texture and tile data from the deferred lighting pass
    const transparentSettings: RenderSettings = {
      mode: RenderMode.Transparent,
      lights,
      lightDataTexture: shared.lightDataTexture,
      tileDataTexture: shared.tileDataTexture,
      tilesX: shared.tilesX,
      tileSize: shared.tileSize,
      objects: transparentObjects,
      volumetricTexture: volumetricRan ? this.volumetricPass.resultTexture : undefined,
      sceneDepthTexture: this.gBufferPass.gBuffer.depthTexture,
      invViewProjection: camera.invViewProjection,
      screenSize: [ctx.viewPort.width, ctx.viewPort.height],
    };

    for (const obj of transparentObjects) {
      obj.render(ctx, camera, transparentSettings);
    }

    gl.disable(gl.BLEND);
    gl.enable(gl.CULL_FACE);
  }

  private sortBackToFront(objects: DrawableObject[], camera: Camera): void {
    const cameraPos = camera.cameraPosition;
    // Pre-compute O(n) distances, then sort — avoids sqrt inside the O(n log n) comparator.
    const tagged = objects.map((obj) => ({
      obj,
      dist: obj.bounds?.boundingSphere
        ? Math.sqrt(squaredDist(obj.bounds.boundingSphere.position, cameraPos)) -
          obj.bounds.boundingSphere.radius
        : 0,
    }));
    tagged.sort((a, b) => b.dist - a.dist);
    for (let i = 0; i < objects.length; i++) objects[i] = tagged[i].obj;
  }
}

function squaredDist(a: { [index: number]: number }, b: { [index: number]: number }): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}
